import {
    Notification,
    RecurrencePattern,
    UserNotification,
} from '@/common/types/databaseTypes';
import { getNextExecutionTime } from '@/common/utils/dateUtils';
import { DELETED_USER_ID, Json } from '@tickitacka/shared';
import { supabase, i18n } from '@tickitacka/shared';
import {
    FeedbackReactionContent,
    getDefaultRelatedType,
    getNotificationMessage,
    NotificationContent,
    NotificationType,
    NotificationTypeToContent,
} from '../constants/notification';
import { useAuthStore } from '@/stores/authStore';

export type JsonCompatible<T> = T & Json;

// Trans 컴포넌트의 props 타입 정의
interface TransProps {
    i18nKey?: string;
    values?: Record<string, unknown>;
    components?: React.ReactNode[];
}

class NotificationService {
    // ==================== 알림 조회, 생성, 업데이트 메서드 ====================

    /**
     * 알림 생성 기본 메서드, 외부에서는 아래의 메서드를 사용하고, 이 함수는 private 메서드로 사용
     * 타입 안정성 확보를 위해 아래 메서드를 사용
     * @param userId 알림 수신자 ID
     * @param type 알림 타입
     * @param content 알림 콘텐츠
     * @param relatedId 알림과 관련된 데이터 ID
     * @param relatedType 알림과 관련된 데이터 타입
     * @returns 알림 생성 성공 여부
     */
    private async createNotification(
        userId: string,
        type: NotificationType,
        content: JsonCompatible<NotificationContent>,
        relatedId?: string,
        relatedType?: string
    ): Promise<Notification | null> {
        const { user } = useAuthStore.getState();
        if (!user?.id || user.id === userId || user.id === DELETED_USER_ID)
            return null; // 자기 자신 및 존재하지 않는 사용자에게 알림 보내지 않음

        const notificationId = crypto.randomUUID();

        const { error } = await supabase.from('notification').insert({
            id: notificationId,
            user_id: userId,
            content,
            type,
            related_id: relatedId,
            related_type: relatedType,
            is_read: false,
            is_viewed: false,
            enable: true,
            register_date: new Date().toISOString(),
            update_date: new Date().toISOString(),
        });

        if (error) {
            console.error('알림 생성 오류:', error);
            return null;
        }

        // 푸시 알림 전송
        this.sendPushNotification({
            userId,
            type,
            notificationId,
            relatedId,
            relatedType,
            content: content as Record<string, unknown>,
        }).catch(error => {
            // 푸시 알림 전송 실패는 알림 생성 자체에 영향을 주지 않음
            console.error('푸시 알림 전송 중 오류 발생:', error);
        });
    }

    /**
     * 알림 생성 메서드 - 제네릭을 활용해 타입 안정성을 확보
     * @param userId 알림 수신자 ID
     * @param type 알림 타입
     * @param content 알림 콘텐츠
     * @param relatedId 알림과 관련된 데이터 ID
     * @returns 알림 생성 성공 여부
     */
    async createTypedNotification<T extends NotificationType>(
        userId: string,
        type: T,
        content: NotificationTypeToContent[T],
        relatedId: string
    ): Promise<Notification | null> {
        return this.createNotification(
            userId,
            type,
            content as unknown as JsonCompatible<NotificationContent>,
            relatedId,
            getDefaultRelatedType(type)
        );
    }

    /**
     * 알림 페이지네이션 조회 (무한스크롤로 구현된 알림 목록 컴포넌트에서 사용)
     * @param userId 알림을 조회할 사용자 ID
     * @param pageParam 페이지 번호 (1부터 시작)
     * @param pageSize 페이지 크기 (한 페이지에 보여질 알림 개수)
     * @returns 알림 목록
     */
    async getPaginatedNotifications(
        userId: string,
        pageParam: number,
        pageSize: number
    ): Promise<Notification[]> {
        const startIndex = (pageParam - 1) * pageSize;
        const endIndex = pageParam * pageSize - 1;

        const { data, error } = await supabase
            .from('notification')
            .select('*')
            .eq('user_id', userId)
            .eq('enable', true)
            .order('register_date', { ascending: false })
            .range(startIndex, endIndex);

        if (error) {
            console.error('알림 조회 오류:', error);
            return [];
        }

        return data.map(item => ({
            ...item,
            content: item.content as unknown as NotificationContent,
        })) as unknown as Notification[];
    }

    /**
     * 알림 컨텐츠 업데이트
     * @param notificationId 알림 ID
     * @param content 알림 컨텐츠
     * @returns 알림 업데이트 성공 여부
     */
    async updateNotificationContent(
        notificationId: string,
        content: JsonCompatible<NotificationContent>
    ): Promise<Notification | null> {
        const { data, error } = await supabase
            .from('notification')
            .update({
                content,
                update_date: new Date().toISOString(),
            })
            .eq('id', notificationId);

        if (error) {
            console.error('알림 업데이트 오류:', error);
            return null;
        }

        return data;
    }

    // ==================== 알림 및 알림 스케줄 삭제 메서드 ====================

    /**
     * 알림 삭제 - 알림의 ID를 통해 삭제
     * @param notificationId 알림 ID
     * @returns 알림 삭제 처리 성공 여부
     */
    async deleteNotificationWithId(notificationId: string): Promise<boolean> {
        const { error } = await supabase
            .from('notification')
            .update({ enable: false })
            .eq('id', notificationId);

        if (error) {
            console.error('알림 삭제 오류:', error);
            return false;
        }

        return true;
    }

    /**
     * 특정 조건을 만족하는 알림을 삭제하는 메서드
     * @param userId 알림 수신자 ID
     * @param relatedId 알림과 관련된 데이터 ID
     * @param type 알림 타입
     * @param registerId 알림 등록자 ID (선택적)
     * @returns 알림 삭제 처리 성공 여부
     */
    async deleteNotificationWithRelatedData({
        userId,
        relatedId,
        type,
        registerId,
    }: {
        userId: string;
        relatedId: string;
        type: NotificationType;
        registerId?: string;
    }): Promise<boolean> {
        let filterBuilder = supabase
            .from('notification')
            .update({ enable: false })
            .eq('user_id', userId)
            .eq('related_id', relatedId)
            .eq('type', type);

        // registerId가 제공된 경우에만 해당 필터 추가
        if (registerId) {
            filterBuilder = filterBuilder.eq('register_id', registerId);
        }

        const { error } = await filterBuilder;

        if (error) {
            console.error('알림 삭제 오류:', error);
            throw error;
        }
        return true;
    }

    /**
     * 알림 스케줄 삭제
     * @param relatedType 알림의 관련 데이터 타입 (테이블명) ex) 'goal', 'project', 'todo'
     * @param relatedId 알림의 관련 데이터 ID
     * @param notificationType 알림 타입 (NotificationType)
     */
    async deleteSchedule({
        relatedType,
        relatedId,
        notificationType,
    }: {
        relatedType: 'goal' | 'project' | 'todo';
        relatedId: string;
        notificationType: NotificationType;
    }) {
        const { error } = await supabase
            .from('notification_schedule')
            .delete()
            .match({
                related_type: relatedType,
                related_id: relatedId,
                notification_type: notificationType,
            });
        if (error) throw error;
    }

    // ==================== 알림 읽음 및 조회 관련 메서드 ====================

    /**
     * 알림 상태 업데이트 - 조회/읽음 처리
     * @param notificationId 알림 ID
     * @param isRead 읽음 여부
     * @param isViewed 조회 완료 여부
     * @returns 알림 상태 업데이트 성공 여부
     */
    async updateNotificationReadStatus(
        notificationId: string,
        isRead: boolean,
        isViewed: boolean
    ): Promise<boolean> {
        const { error } = await supabase
            .from('notification')
            .update({
                is_read: isRead,
                is_viewed: isViewed,
                update_date: new Date().toISOString(),
            })
            .eq('id', notificationId);

        if (error) {
            console.error('알림 업데이트 오류:', error);
            return false;
        }

        return true;
    }

    /**
     * 모든 알림을 읽음 처리
     * @param userId 알림 수신자 ID
     * @returns 알림 읽음 처리 성공 여부
     */
    async markAllAsRead(userId: string): Promise<boolean> {
        const { error } = await supabase
            .from('notification')
            .update({
                is_read: true,
                is_viewed: true,
                update_date: new Date().toISOString(),
            })
            .eq('user_id', userId)
            .eq('is_read', false);

        if (error) {
            console.error('모든 알림 읽음 처리 오류:', error);
            return false;
        }

        return true;
    }

    /**
     * 모든 알림을 조회 완료 상태로 표시
     * @param userId 알림 수신자 ID
     * @returns 알림 조회 완료 처리 성공 여부
     */
    async markAllAsViewed(userId: string): Promise<boolean> {
        const { error } = await supabase
            .from('notification')
            .update({
                is_viewed: true,
                update_date: new Date().toISOString(),
            })
            .eq('user_id', userId)
            .eq('is_viewed', false);

        if (error) {
            console.error('모든 알림 조회 완료 처리 오류:', error);
            return false;
        }

        return true;
    }

    // ==================== 계획 수립 알림 설정 관련 메서드 ====================

    /**
     * 계획 수립 알림 설정 여부 조회
     * @param userId 알림 수신자 ID
     * @returns 계획 수립 알림 설정 여부
     */
    async getPlanTimeSetting(userId: string): Promise<UserNotification | null> {
        try {
            const { data, error } = await supabase
                .from('user_notification')
                .select('*')
                .eq('user_id', userId)
                .eq('notification_type_code', NotificationType.PLAN_TIME)
                .eq('enable', true)
                .maybeSingle();

            if (error) throw error;

            if (!data) return null;

            return {
                ...data,
                recurrence_pattern:
                    data.recurrence_pattern as unknown as RecurrencePattern,
            };
        } catch (error) {
            console.error('알림 설정 조회 중 오류:', error);
            return null;
        }
    }

    /**
     * 계획 수립 알림 설정 추가
     * @param userId 알림 수신자 ID
     * @param time 알림 발송 시간
     * @param weekday 알림 발송 요일
     */
    async upsertPlanTimeSetting({
        userId,
        time,
        weekday,
    }: {
        userId: string;
        time: string;
        weekday: number[];
    }) {
        const nextExecutionTime = getNextExecutionTime(weekday, time);
        const { error } = await supabase.from('user_notification').upsert(
            {
                user_id: userId,
                notification_type_code: NotificationType.PLAN_TIME,
                recurrence_pattern: {
                    type: 'weekly', // 일단 고정
                    time,
                    weekday,
                },
                next_execution_time: nextExecutionTime,
                register_date: new Date().toISOString(),
                register_id: userId,
                update_date: new Date().toISOString(),
                update_id: userId,
                enable: true,
            },
            {
                onConflict: 'user_id,notification_type_code',
                ignoreDuplicates: false, // 중복 시 업데이트
            }
        );

        if (error) throw error;
    }

    /**
     * 계획 수립 알림 설정 비활성화
     * @param userId 알림 수신자 ID
     */
    async disablePlanTimeSetting({ userId }: { userId: string }) {
        const { error } = await supabase
            .from('user_notification')
            .update({
                enable: false,
                update_date: new Date().toISOString(),
                update_id: userId,
            })
            .eq('user_id', userId)
            .eq('notification_type_code', NotificationType.PLAN_TIME);
        if (error) throw error;
    }

    // ==================== 피드백 이모지 리액션 관련 메서드 ====================
    /**
     * 피드백 이모지 리액션 알림 조회 (제일 최신 알림 1개만 조회)
     * @param userId 알림 수신자 ID
     * @param feedbackId 피드백 ID
     * @returns 알림 조회 결과 (알림 존재 여부, 읽음 여부, 알림 ID)
     */
    async checkExistingFeedbackReactionNotification({
        userId,
        feedbackId,
    }: {
        userId: string;
        feedbackId: string;
    }): Promise<{
        isExisting: boolean;
        isRead: boolean;
        notificationId?: string;
        reactionEmojiCodes?: string[];
    } | null> {
        if (!userId || userId === DELETED_USER_ID) {
            console.warn('사용자가 존재하지 않아 알림 조회를 건너뜁니다.');
            return null;
        }

        try {
            const { data, error } = await supabase
                .from('notification')
                .select('id, is_read, content')
                .eq('user_id', userId)
                .eq('related_type', 'feedback')
                .eq('related_id', feedbackId)
                .eq('type', NotificationType.FEEDBACK_REACTION)
                .eq('enable', true)
                .order('register_date', { ascending: false })
                .limit(1)
                .maybeSingle();

            if (error) throw error;

            if (!data) {
                return { isExisting: false, isRead: false };
            }

            return {
                isExisting: true,
                isRead: data.is_read,
                notificationId: data.id,
                reactionEmojiCodes: (
                    data.content as unknown as FeedbackReactionContent
                ).reaction_content,
            };
        } catch (error) {
            console.error('기존 알림 조회 실패:', error);
            return null;
        }
    }

    // ==================== 푸시 알림 전송 관련 메서드 ====================

    /**
     * fallback 메시지 생성
     */
    private getFallbackMessage(type: NotificationType): string {
        const DEFAULT_MESSAGE = i18n.t('notification:defaultMessage');
        return i18n.t(`notification:${type}`) || DEFAULT_MESSAGE;
    }

    /**
     * Trans 컴포넌트에서 i18n 키와 값 추출
     */
    private extractTransProps(reactComponent: unknown): TransProps | null {
        if (
            !reactComponent ||
            typeof reactComponent !== 'object' ||
            !('props' in reactComponent) ||
            !(reactComponent as { props?: unknown }).props
        ) {
            return null;
        }

        const componentWithProps = reactComponent as { props: unknown };
        const props = componentWithProps.props as TransProps;
        return props?.i18nKey ? props : null;
    }

    /**
     * 컨텐츠로부터 푸시 알림 메시지 생성
     */
    private createMessageFromContent(
        type: NotificationType,
        content: Record<string, unknown>
    ): string {
        try {
            const messageResult = getNotificationMessage(
                type,
                content as unknown as NotificationContent
            );

            if (!messageResult?.message) {
                return this.getFallbackMessage(type);
            }

            const transProps = this.extractTransProps(messageResult.message);
            if (!transProps) {
                return this.getFallbackMessage(type);
            }

            // i18nKey를 사용하여 번역 및 HTML 태그 제거
            const translatedText = i18n.t(
                transProps.i18nKey,
                transProps.values || {}
            );

            return translatedText.replace(/<\/?\d+>/g, '');
        } catch (error) {
            console.error('알림 메시지 생성 오류:', error);
            return this.getFallbackMessage(type);
        }
    }

    /**
     * Supabase Edge Function을 호출하여 푸시 알림 전송
     * 푸시 알림 전송을 위한 별도의 메서드
     * @param userId 사용자 ID
     * @param message 알림 메시지
     * @param type 알림 타입
     * @param data 추가 데이터
     * @returns 성공 여부
     */
    async sendPushNotification({
        userId,
        message: customMessage,
        type,
        notificationId,
        relatedId,
        relatedType,
        content,
    }: {
        userId: string;
        message?: string;
        type: NotificationType;
        notificationId?: string;
        relatedId?: string;
        relatedType?: string;
        content?: Record<string, unknown>;
    }): Promise<boolean> {
        if (!userId || userId === DELETED_USER_ID) {
            console.warn('사용자가 존재하지 않아 푸시 알림 전송을 건너뜁니다.');
            return false;
        }

        try {
            let message = customMessage;

            if (!message && content && typeof content === 'object') {
                message = this.createMessageFromContent(type, content);
            }

            if (!message) {
                message = this.getFallbackMessage(type);
            }

            // 메시지 문자열 변환
            if (typeof message !== 'string') {
                message = JSON.stringify(message);
            }

            // Edge Function 호출
            await supabase.functions.invoke('notification', {
                body: {
                    userId,
                    message,
                    type,
                    data: {
                        notificationId,
                        relatedId,
                        relatedType,
                        content: content || {},
                    },
                },
            });

            return true;
        } catch (pushError) {
            console.error(
                '푸시 알림 전송 오류:',
                pushError instanceof Error
                    ? pushError.message
                    : String(pushError)
            );
            return false;
        }
    }
}

// ==================== 인스턴스 생성 및 내보내기 ====================
export const notificationService = new NotificationService();
